import { useCallback, useEffect, useState } from 'react';
import { fetchAdminHiddenPointsSignals } from '../infrastructure/adminApi';

const formatDate = (value) => new Date(value).toLocaleString('es-MX', {
  dateStyle: 'medium', timeStyle: 'short',
});

const formatPointers = (pointerTypes) => Object.entries(pointerTypes || {})
  .map(([type, count]) => `${type} ${count}`)
  .join(' · ');

export default function HiddenPointsSignalsPanel({ authorization, onSessionExpired }) {
  const [players, setPlayers] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await fetchAdminHiddenPointsSignals(authorization);
      setPlayers(result.players || []);
    } catch (requestError) {
      if (requestError.status === 401) onSessionExpired?.();
      else setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [authorization, onSessionExpired]);

  useEffect(() => { load(); }, [load]);

  return (
    <section className="mx-auto max-w-[1480px] px-5 pb-8 lg:px-8">
      <div className="border border-[#312a3a] bg-[#121019]/80 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-display text-2xl uppercase text-white">Señales de hallazgos (7 días)</h3>
            <p className="mt-1 text-xs leading-5 text-[#afa2b9]">
              Sesiones, redes y gestos por cuenta. Un gesto muy constante (CV bajo), toques exactos al centro o muchas
              redes a la vez merecen revisión; no prueban trampa por sí solos.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="min-h-[44px] border border-[#594a65] px-4 py-2 text-xs text-white disabled:opacity-50"
          >
            {loading ? 'Cargando…' : 'Actualizar'}
          </button>
        </div>
        {error && <p className="mt-4 text-sm text-[#ffd8de]" role="alert">{error}</p>}
        {!loading && !error && players.length === 0 && (
          <p className="mt-4 text-sm text-[#afa2b9]">Aún no hay hallazgos con señales registradas.</p>
        )}
        {players.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[860px] text-left text-xs text-[#eee8f5]">
              <thead className="border-b border-[#3a3344] text-[#afa2b9]">
                <tr>
                  <th className="px-3 py-2">Jugador</th>
                  <th className="px-3 py-2">Hallazgos</th>
                  <th className="px-3 py-2">Sesiones</th>
                  <th className="px-3 py-2">Redes</th>
                  <th className="px-3 py-2">Navegadores</th>
                  <th className="px-3 py-2">Puntero</th>
                  <th className="px-3 py-2">Gesto medio</th>
                  <th className="px-3 py-2">CV gesto</th>
                  <th className="px-3 py-2">Al centro</th>
                  <th className="px-3 py-2">Último</th>
                </tr>
              </thead>
              <tbody>
                {players.map((player) => (
                  <tr key={player.playerId} className="border-b border-[#26202d]">
                    <td className="px-3 py-2">{player.username} (#{player.playerId})</td>
                    <td className="px-3 py-2">{player.finds}</td>
                    <td className="px-3 py-2">{player.sessions}</td>
                    <td className="px-3 py-2">{player.networks}</td>
                    <td className="px-3 py-2">{player.userAgents}</td>
                    <td className="px-3 py-2">{formatPointers(player.pointerTypes)}</td>
                    <td className="px-3 py-2">{player.gestureMeanMs} ms</td>
                    <td className="px-3 py-2">{player.gestureCv ?? '—'}</td>
                    <td className="px-3 py-2">{player.centered}/{player.finds - player.missingOffset}</td>
                    <td className="px-3 py-2">{formatDate(player.lastAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
